<!-- Mirrors CopilotBar.svelte in the scripts repo -->
<template>
  <div
    class="flex items-center justify-between gap-4 bg-white p-4 text-xs text-paragraph"
    :style="buttonStyles"
  >
    <div class="flex min-w-0 items-center gap-2">
      <img
        v-if="logo?.full_url"
        :src="logo.full_url"
        :alt="displayName"
        class="h-6 max-w-40 shrink-0"
      />
      <img
        v-if="personalPhoto?.full_url"
        :src="personalPhoto.full_url"
        :alt="displayName"
        class="size-6 shrink-0 rounded-full object-cover"
      />
      <span class="line-clamp-2">{{ displayName }}</span>
    </div>

    <div class="flex shrink-0 items-center gap-1.5 md:gap-4">
      <template v-if="phone">
        <span class="grid size-7 place-items-center rounded-[--button-radius] ring-1 ring-inset ring-gray-300 md:hidden">
          <Icon icon="phone" />
        </span>
        <span class="hidden items-center gap-1.5 md:flex">
          <Icon icon="phone" />
          {{ phone }}
        </span>
      </template>

      <span class="copilot-button grid size-7 place-items-center md:hidden">
        <Icon icon="envelope" />
      </span>
      <span class="copilot-button hidden px-2.5 py-2 text-[11px] leading-none md:block">
        {{ $t('contact') }}
      </span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Media } from '~/types/media'

export interface CopilotHeaderProps {
  firstName: string | null
  lastName: string | null
  phone: string | null
  logo: Pick<Media, 'full_url'> | null
  personalPhoto: Pick<Media, 'full_url'> | null
  styles?: Record<string, string>
}

const props = defineProps<CopilotHeaderProps>()

const displayName = computed(() => [props.firstName, props.lastName].filter(Boolean).join(' ') || 'Your Name')

// Partner fonts aren't loaded here, so the button keeps the page font
const buttonStyles = computed(() => {
  const styles = props.styles ?? {}
  const primary = styles.primary_color ?? '#0377b4'

  return {
    '--button-background': styles.button_primary_solid_background_color ?? primary,
    '--button-color': styles.button_primary_solid_color ?? 'white',
    '--button-radius': styles.button_border_radius ?? '0.375rem',
    '--button-font-weight': styles.button_font_weight ?? '700',
    '--button-letter-spacing': styles.button_letter_spacing ?? '0',
    '--button-text-transform': styles.button_text_transform ?? 'none'
  }
})
</script>

<style>
.copilot-button {
  border-radius: var(--button-radius);
  background-color: var(--button-background);
  color: var(--button-color);
  font-weight: var(--button-font-weight);
  letter-spacing: var(--button-letter-spacing);
  text-transform: var(--button-text-transform);
  /* Trim the line box to the cap height so the label centres optically, not by font metrics */
  text-box: trim-both cap alphabetic;
}
</style>
